/* ═══════════════════════════════════════════════════════════════
 * PrivacyControlPanel.tsx
 *
 * The "Control Panel" landing surface for the Privacy Suite —
 * a window full of icons (Send / Receive / Tools per coin) just
 * like the Win 3.1 Control Panel. Inspired by Encarta's start
 * shelves and Win 8 Metro tiles, but kept rectangular + beveled
 * so it still belongs in the WINBIT32 desktop.
 *
 * The data model is intentionally light: a list of GROUPS, each
 * with a label and an ordered list of `progName` strings. The
 * component looks each `progName` up in the sub-programs array
 * passed to the suite (via `windowA.programs`) and renders one
 * tile per match.
 *
 * That keeps the surface fully data-driven: anything declared in
 * `programs.js` automatically becomes a clickable tile here, and
 * the tile uses the same `icon`, `title`, `titleNote` fields the
 * Program Manager uses. No duplicate copy to keep in sync.
 *
 * Component responsibilities:
 *
 *   • Render the header (title + status pill).
 *   • Render the icon grid grouped by category.
 *   • Call back `onLaunch(progName)` when a tile is clicked.
 *
 * Visual styling lives in `privacyTheme.css` and respects the
 * active theme via the CSS variables on `<html>`. The component
 * doesn't know or care which theme is active — that's the
 * provider's job.
 * ═══════════════════════════════════════════════════════════════ */

import { useMemo, type ReactNode } from 'react';

/**
 * A program entry as understood by the WINBIT32 program manager.
 *
 * Only the fields the Control Panel actually reads are listed
 * here — the surrounding code (`programs.js`) supplies many more
 * fields (component, addProgramData, initialPosition, ...), but
 * we deliberately don't depend on them so the type stays stable
 * across program-shape changes.
 */
export interface PrivacyControlPanelProgram {
	readonly progName: string;
	readonly title: string;
	readonly titleNote?: string;
	readonly icon?: ReactNode;
	readonly tooltip?: string;
}

/**
 * A group of programs in the Control Panel.
 *
 * `ids` is an ORDERED list of `progName` strings; the component
 * preserves that order when rendering. Programs not present in
 * the `programs` array are silently skipped so a missing program
 * (e.g. work in progress, feature flag off) never crashes the
 * suite.
 */
export interface PrivacyControlPanelGroup {
	readonly id: string;
	readonly label: string;
	readonly ids: readonly string[];
	/** Optional one-liner shown under the group label. */
	readonly description?: string;
	/**
	 * Optional accent token for the group frame. Resolves to a CSS
	 * class — currently `'zec' | 'xmr' | 'dash' | 'tools' |
	 * 'ironwood'`. Used to colour the group border + label so the
	 * user can spot Zcash vs Monero vs Dash (vs the Ironwood NU7
	 * feature shelf) at a glance.
	 */
	readonly accent?: 'zec' | 'xmr' | 'dash' | 'tools' | 'ironwood';
}

export interface PrivacyControlPanelStatus {
	readonly connected: boolean;
	/** Free-form description rendered to the right of the dot. */
	readonly label: string;
	/** Optional truncated address shown on the far right. */
	readonly address?: string;
}

export interface PrivacyControlPanelProps {
	/** Sub-programs exposed by the suite (`windowA.programs`). */
	readonly programs: readonly PrivacyControlPanelProgram[];
	/** Group definitions. Programs not in any group are dropped. */
	readonly groups: readonly PrivacyControlPanelGroup[];
	/** Header copy. */
	readonly title?: string;
	readonly subtitle?: string;
	/** Optional wallet status pill above the grid. */
	readonly status?: PrivacyControlPanelStatus | null;
	/** Optional extra controls (e.g. info ⓘ button). */
	readonly headerActions?: ReactNode;
	/** Tile click handler. */
	readonly onLaunch: (progName: string) => void;
}

/**
 * Build a lookup map for the program array. Memoised by the
 * caller (we accept the programs array unchanged so the parent
 * can refresh tiles by re-rendering with a different list).
 */
function indexPrograms(
	programs: readonly PrivacyControlPanelProgram[],
): Map<string, PrivacyControlPanelProgram> {
	const out = new Map<string, PrivacyControlPanelProgram>();
	for (const program of programs) {
		out.set(program.progName, program);
	}
	return out;
}

/**
 * Helper for rendering a single tile. Pulled out for unit-test
 * friendliness — the test file can construct one tile in
 * isolation without bringing the whole grid.
 */
export interface PrivacyControlPanelTileProps {
	readonly program: PrivacyControlPanelProgram;
	readonly onClick: () => void;
}

export function PrivacyControlPanelTile({ program, onClick }: PrivacyControlPanelTileProps) {
	return (
		<button
			type="button"
			className="privacy-control-panel__tile"
			title={program.tooltip || program.title}
			onClick={onClick}
		>
			<span className="privacy-control-panel__tile-icon" aria-hidden="true">
				{program.icon ?? '◾'}
			</span>
			<span className="privacy-control-panel__tile-title">{program.title}</span>
			{program.titleNote && (
				<span className="privacy-control-panel__tile-desc">{program.titleNote}</span>
			)}
		</button>
	);
}

/**
 * Top-level Control Panel surface. Stateless — the parent owns
 * the data and the click handler. Renders nothing if there are
 * no resolvable programs across any group, so the suite collapses
 * gracefully when used in a test fixture that doesn't ship sub-
 * windows.
 */
export function PrivacyControlPanel({
	programs,
	groups,
	title = 'Privacy Suite',
	subtitle,
	status,
	headerActions,
	onLaunch,
}: PrivacyControlPanelProps) {
	const programIndex = useMemo(() => indexPrograms(programs), [programs]);

	const resolvedGroups = useMemo(() => {
		return groups
			.map((group) => ({
				...group,
				items: group.ids
					.map((id) => programIndex.get(id))
					.filter((p): p is PrivacyControlPanelProgram => Boolean(p)),
			}))
			.filter((group) => group.items.length > 0);
	}, [groups, programIndex]);

	if (resolvedGroups.length === 0) {
		return null;
	}

	const statusClass = status
		? `privacy-control-panel__status privacy-control-panel__status--${
			status.connected ? 'connected' : 'disconnected'
		}`
		: '';

	return (
		<div className="privacy-control-panel">
			<header className="privacy-control-panel__header">
				<h2 className="privacy-control-panel__title">{title}</h2>
				{subtitle && (
					<p className="privacy-control-panel__subtitle">{subtitle}</p>
				)}
				{headerActions && (
					<div className="privacy-control-panel__header-actions">
						{headerActions}
					</div>
				)}
			</header>

			{status && (
				<div className={statusClass} role="status" aria-live="polite">
					<span className="privacy-control-panel__status-dot" aria-hidden="true" />
					<span>{status.label}</span>
					{status.address && (
						<span className="privacy-control-panel__status-addr">
							{status.address}
						</span>
					)}
				</div>
			)}

			{resolvedGroups.map((group) => (
				<section
					key={group.id}
					className={
						'privacy-control-panel__group'
						+ (group.accent ? ` privacy-control-panel__group--${group.accent}` : '')
					}
					aria-label={group.label}
				>
					<h3 className="privacy-control-panel__group-header">{group.label}</h3>
					{group.description && (
						<p className="privacy-control-panel__group-desc">{group.description}</p>
					)}
					<div className="privacy-control-panel__grid">
						{group.items.map((program) => (
							<PrivacyControlPanelTile
								key={program.progName}
								program={program}
								onClick={() => onLaunch(program.progName)}
							/>
						))}
					</div>
				</section>
			))}
		</div>
	);
}

PrivacyControlPanel.displayName = 'PrivacyControlPanel';

/**
 * Default group layout for the Privacy Suite. Exported so callers
 * can extend or reorder it without rebuilding the component.
 *
 * Group composition is hard-coded against the curated set of
 * sub-programs the Privacy Suite ships; new programs (e.g. an
 * Activity Browser) should be added either here or in a caller-
 * supplied array.
 */
/**
 * Default Privacy Suite tile layout.
 *
 * Grouped per-chain so users can identify Zcash vs Monero at a
 * glance via the coloured group frame defined in
 * `privacyTheme.css`. The shared "Tools" row keeps things that are
 * chain-agnostic (Privacy Prompt CLI, both TX inspectors, the
 * unified Vault Wizard).
 *
 * Group composition is hard-coded against the curated set of
 * sub-programs the Privacy Suite ships; new programs should be
 * added either here or in a caller-supplied array.
 */
export const DEFAULT_PRIVACY_GROUPS: readonly PrivacyControlPanelGroup[] = [
	{
		// Ironwood (NU7) feature shelf — first while the upgrade is
		// topical. Encarta-96-styled frame via the 'ironwood' accent.
		id: 'ironwood',
		label: 'Ironwood · Network Upgrade 7',
		description: 'Zcash’s new shielded pool arrives 28 July — check your funds are ready to cross.',
		accent: 'ironwood',
		ids: [
			'ironwood.exe',
			'zecstats.exe',
		],
	},
	{
		id: 'zec',
		label: 'Zcash',
		accent: 'zec',
		ids: [
			'zcashwzrd.exe',
			'zcashrecv.exe',
			'zcashtxi.exe',
			'zcashbus.exe',
			'purse.exe',
			'nfpt.exe',
		],
	},
	{
		id: 'xmr',
		label: 'Monero',
		accent: 'xmr',
		ids: [
			'monerosend.exe',
			'monerorecv.exe',
			'monerotxi.exe',
		],
	},
	// Dash group removed for now: the denomination viewer cannot actually
	// run CoinJoin mixing (needs Dash Core's masternode session protocol),
	// so it stays unregistered until it can be done properly.
	{
		id: 'tools',
		label: 'Tools',
		accent: 'tools',
		ids: [
			'zcashcli.exe',
			'createvault.exe',
		],
	},
];
